import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin } from 'lucide-react';
import { ADDRESS, EMAIL, LEGAL, PHONE_HREF, PHONE_NUMBER } from '../../siteConfig';

const linkClass =
  'focus-ring-dark inline-flex min-h-[36px] items-center gap-2 rounded-btn text-slate-300 no-underline transition-colors duration-150 hover:text-taxi lg:min-h-0';

function Group({ title, children, className = '' }) {
  return (
    <div className={className}>
      <h2 className="m-0 text-sm font-bold uppercase tracking-[0.12em] text-white">{title}</h2>
      <ul className="m-0 mt-3 flex list-none flex-col items-center gap-1 p-0 lg:mt-6 lg:items-start lg:gap-3.5">{children}</ul>
    </div>
  );
}

// Desktop: logo zone (1/3) + three link columns (2/3). Phone: centred logo,
// then Quick Links and Hours side by side with Contact underneath, to keep
// the footer short. Staff login is deliberately not linked anywhere.
function SiteFooter() {
  const { t } = useTranslation();

  return (
    <footer id="contact" className="bg-navy-deep pb-24 pt-10 font-sans text-base lg:pb-24 lg:pt-20">
      <div className="site-container grid gap-8 text-center lg:grid-cols-3 lg:gap-12 lg:text-left">
        {/* Logo and name centred over the tagline; the block sits left on desktop. */}
        <div className="flex w-fit flex-col items-center justify-self-center text-center lg:justify-self-start">
          <Link to="/" className="focus-ring-dark rounded-btn" aria-label="TaxiReady2Go">
            <img src="/logo-mark.png" alt="" width="97" height="80" className="block h-16 w-auto lg:h-20" loading="lazy" />
          </Link>
          <p className="m-0 mt-3 text-2xl font-bold tracking-tight text-white">TaxiReady2Go</p>
          <p className="m-0 mt-1.5 max-w-xs leading-relaxed text-slate-300">{t('footer.tagline')}</p>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:grid-cols-[repeat(3,auto)] sm:justify-between sm:gap-12 lg:col-span-2">
          <nav aria-label={t('footer.quickLinks')}>
            <Group title={t('footer.quickLinks')}>
              <li><Link to="/services" className={linkClass}>{t('navbar.services')}</Link></li>
              <li><Link to="/rates" className={linkClass}>{t('navbar.rates')}</Link></li>
              <li><Link to="/about" className={linkClass}>{t('navbar.about')}</Link></li>
            </Group>
          </nav>

          <Group title={t('footer.contactUs')} className="order-last col-span-2 sm:order-none sm:col-span-1">
            <li>
              <a href={PHONE_HREF} className={linkClass}>
                <Phone size={16} aria-hidden="true" className="shrink-0 text-taxi" /> {PHONE_NUMBER}
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className={`${linkClass} break-all sm:break-normal`}>
                <Mail size={16} aria-hidden="true" className="shrink-0 text-taxi" /> {EMAIL}
              </a>
            </li>
            <li className="flex min-h-[36px] items-center gap-2 text-slate-300 lg:min-h-0">
              <MapPin size={16} aria-hidden="true" className="shrink-0 text-taxi" /> {ADDRESS}
            </li>
          </Group>

          <Group title={t('footer.hours')}>
            <li className="flex min-h-[36px] items-center text-slate-300 lg:min-h-0">{t('footer.hours247')}</li>
            <li className="flex min-h-[36px] items-center text-slate-300 lg:min-h-0">{t('footer.hoursEveryday')}</li>
            <li className="flex min-h-[36px] items-center text-slate-300 lg:min-h-0">{t('footer.hoursAnytime')}</li>
          </Group>
        </div>
      </div>

      <div className="site-container mt-8 lg:mt-12">
        <div className="flex flex-col items-center gap-2 border-0 border-t border-solid border-white/10 pt-6 text-center text-sm text-slate-300 lg:pt-8">
          <p className="m-0">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          {LEGAL.businessName && LEGAL.nipt && (
            <p className="m-0">
              {LEGAL.businessName} · NIPT {LEGAL.nipt}
            </p>
          )}
          <nav aria-label={t('site.legal.label')}>
            <ul className="m-0 flex list-none flex-wrap justify-center gap-x-5 gap-y-1 p-0">
              <li><Link to="/privacy" className={linkClass}>{t('site.legal.privacy')}</Link></li>
              <li><Link to="/terms" className={linkClass}>{t('site.legal.terms')}</Link></li>
              <li><Link to="/cookies" className={linkClass}>{t('site.legal.cookies')}</Link></li>
            </ul>
          </nav>
          <a
            href="https://ridgeabove.com"
            target="_blank"
            rel="noreferrer"
            className="focus-ring-dark mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-solid border-white/[0.07] bg-white/[0.02] px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] no-underline transition-colors hover:border-white/20 hover:bg-white/[0.06]"
          >
            <span>
              <span className="text-[#8b5cf6]">Powered</span> <span className="text-[#f97316]">by</span>
            </span>
            <img src="/images/ridge-logo.png" alt="Ridge" width="35" height="20" loading="lazy" decoding="async" className="h-5 w-auto" />
          </a>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
